import { useMemo, useState } from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import { FleetMap } from '../components/FleetMap'
import {
  IconBack,
  IconBattery,
  IconBolt,
  IconChart,
  IconCompass,
  IconCrown,
  IconFuel,
  IconHistory,
  IconOdo,
  IconPin,
  IconSpeed,
  IconTemp,
} from '../components/Icons'
import { useGetVehiclesQuery } from '../services/vehicle'
import { deriveTelemetryFromPosition, formatDashFr } from '../helpers/telemetry'
import { getFullPath } from '../helpers/path'
import type { ReactNode } from 'react'
import type { Vehicle } from '../types'
import './Details.css'

export function DetailsPage() {
  const { t } = useTranslation()
  const { vehicleId } = useParams()
  const location = useLocation()
  const { data, isLoading } = useGetVehiclesQuery()
  const [managerOpen, setManagerOpen] = useState(false)

  const vehicle: Vehicle | undefined = useMemo(() => {
    const fromState = (location.state as { vehicle?: Vehicle } | null)?.vehicle
    if (fromState && String(fromState._id) === String(vehicleId)) return fromState
    return data?.data?.find((v) => String(v._id) === String(vehicleId))
  }, [data?.data, location.state, vehicleId])

  const tel = useMemo(
    () => deriveTelemetryFromPosition(vehicle?.latestPosition),
    [vehicle?.latestPosition]
  )

  const dash = (v: number | undefined, fmt: (n: number) => string) =>
    v === undefined || Number.isNaN(v) ? t('No data') : fmt(v)

  if (isLoading && !vehicle) return <p className="muted details__pad">{t('Chargement…')}</p>
  if (!vehicle) return <p className="muted details__pad">{t('Aucun véhicule')}</p>

  const lp = vehicle.latestPosition
  const brandIcon = getFullPath('brands', vehicle.brandId?.icon)
  const coordLine =
    lp && Number.isFinite(lp.latitude)
      ? `${lp.latitude.toFixed(6)}, ${lp.longitude.toFixed(6)}`
      : t('No data')
  const mapsUrl =
    lp && Number.isFinite(lp.latitude)
      ? `https://www.google.com/maps/search/?api=1&query=${lp.latitude},${lp.longitude}`
      : undefined
  const wazeUrl =
    lp && Number.isFinite(lp.latitude)
      ? `https://waze.com/ul?ll=${lp.latitude},${lp.longitude}&navigate=yes`
      : undefined
  const planBg = vehicle.planId?.bgColor || '#4576E5'
  const planLabel = vehicle.planId?.name
    ? `Formule ${vehicle.planId.name}${
        vehicle.subscriptionExpireAt
          ? ` : active jusqu’au : ${moment(vehicle.subscriptionExpireAt).format('DD/MM/YYYY')}`
          : ''
      }`
    : t('No data')

  return (
    <div className="details">
      <header className="details__head">
        <Link to="/app" className="details__back" aria-label={t('Suivi')}>
          <IconBack size={18} />
        </Link>
        <h1>{t('Détails véhicule')}</h1>
      </header>

      <div className="details__layout">
        <div className="details__map">
          <FleetMap
            vehicles={[vehicle]}
            focus={lp && Number.isFinite(lp.latitude) ? lp : null}
            height="100%"
          />
          <div className="details__geoctas">
            {mapsUrl && (
              <a href={mapsUrl} target="_blank" rel="noreferrer" title="Google Maps">
                Maps
              </a>
            )}
            {wazeUrl && (
              <a href={wazeUrl} target="_blank" rel="noreferrer" title="Waze">
                Waze
              </a>
            )}
          </div>
        </div>

        <section className="details__panel">
          <div className="details__plan" style={{ background: planBg }}>
            <IconCrown size={18} />
            <span>{planLabel}</span>
          </div>

          <div className="details__veh">
            {brandIcon ? (
              <img src={brandIcon} alt="" className="details__brand" />
            ) : (
              <span className="details__brand details__brand--fallback">?</span>
            )}
            <strong>{vehicle.name}</strong>
            <span className="plate-chip">{vehicle.plateNumber}</span>
          </div>

          <h2>{t('Moteur et conduite')}</h2>
          <div className="tel-group">
            <TelRow icon={<IconBolt size={18} />} label={t('Allumage')}>
              <span
                className={`tel-ign${
                  tel.ignition === 'on' ? ' is-on' : tel.ignition === 'off' ? ' is-off' : ''
                }`}
              >
                {tel.ignition === 'on'
                  ? 'ON'
                  : tel.ignition === 'off'
                    ? 'OFF'
                    : t('No data')}
              </span>
            </TelRow>
            <TelRow icon={<IconTemp size={18} />} label={t('Température moteur')}>
              {dash(tel.engineTempC, (n) => formatDashFr(n, '°C', 0))}
            </TelRow>
            <TelRow icon={<IconOdo size={18} />} label={t('Odomètre')} last>
              {dash(tel.odometerKm, (n) => formatDashFr(n, 'km', 0))}
            </TelRow>
          </div>

          <h2>{t('Localisation et mouvement')}</h2>
          <div className="tel-group">
            <TelRow icon={<IconPin size={18} />} label={t('Position')}>
              <span className="tel-addr">
                <b>{tel.address || t('No data')}</b>
                <small>{coordLine}</small>
              </span>
            </TelRow>
            <TelRow icon={<IconSpeed size={18} />} label={t('Vitesse')}>
              {dash(tel.speedKmh, (n) => `${Math.round(n)} km/h`)}
            </TelRow>
            <TelRow icon={<IconCompass size={18} />} label={t('Cap')} last>
              {dash(tel.angleDeg, (n) => `${Math.round(n)}°`)}
            </TelRow>
          </div>

          <h2>{t('Carburant et énergie')}</h2>
          <div className="tel-group">
            <TelRow icon={<IconFuel size={18} />} label={t('Carburant')}>
              {dash(tel.fuelPct, (n) => formatDashFr(n, '%', 0))}
            </TelRow>
            <TelRow icon={<IconBattery size={18} />} label={t('Batterie véhicule')}>
              {dash(tel.externalVoltageV, (n) => formatDashFr(n, 'V', 1))}
            </TelRow>
            <TelRow icon={<IconBolt size={18} />} label={t('Tension batterie')} last>
              {dash(tel.internalVoltageV, (n) => formatDashFr(n, 'V', 1))}
            </TelRow>
          </div>

          <button type="button" className="details__manage" onClick={() => setManagerOpen(true)}>
            {t('Gérer mon véhicule')}
          </button>
        </section>
      </div>

      {managerOpen && (
        <div className="manager" role="dialog" aria-modal="true">
          <button type="button" className="manager__backdrop" aria-label="Close" onClick={() => setManagerOpen(false)} />
          <div className="manager__sheet">
            <header>
              <h2>{t('Gestion de véhicule')}</h2>
              <button type="button" onClick={() => setManagerOpen(false)}>
                ×
              </button>
            </header>
            <div className="manager__grid">
              <Link to={`/app/reports?vehicleId=${vehicle._id}`} onClick={() => setManagerOpen(false)}>
                <IconChart size={20} />
                {t('Rapports')}
              </Link>
              <Link to={`/app/history?vehicleId=${vehicle._id}`} onClick={() => setManagerOpen(false)}>
                <IconHistory size={20} />
                {t('Historique')}
              </Link>
            </div>
            <p className="muted manager__note">Modifier · Factures · Formule — disponibles sur l’app mobile</p>
          </div>
        </div>
      )}
    </div>
  )
}

function TelRow({
  icon,
  label,
  children,
  last,
}: {
  icon: ReactNode
  label: string
  children: ReactNode
  last?: boolean
}) {
  return (
    <div className={`tel-row${last ? ' is-last' : ''}`}>
      <span className="tel-row__ico">{icon}</span>
      <span className="tel-row__label">{label}</span>
      <div className="tel-row__value">{children}</div>
    </div>
  )
}
